import React, { useEffect, useState } from 'react';

/**
 * Small "v1.0.0" pill rendered in the top nav. Hovering shows the git SHA
 * and the deploy timestamp.
 *
 * Source of the values is the backend's GET /api/version endpoint, which
 * reads from env vars injected by the Helm backend deployment template
 * (see helm/pawtection/templates/deployment-backend.yaml and
 * docs/technical/versioning.md). We fetch on mount and quietly hide on
 * failure — the pill is an observability surface, never a blocker.
 */

interface VersionPayload {
  version: string;
  git_sha: string;
  built_at: string;
}

const resolveApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
    return 'http://localhost:8000/api/';
  }
  return (
    (import.meta as any).env?.VITE_API_URL ||
    'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/'
  );
};

const VersionPill: React.FC = () => {
  const [info, setInfo] = useState<VersionPayload | null>(null);

  useEffect(() => {
    let cancelled = false;
    const url = `${resolveApiBaseUrl()}version`;
    fetch(url, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: VersionPayload) => {
        if (!cancelled) setInfo(data);
      })
      .catch(() => {
        // Quietly skip — never block the UI on an introspection endpoint.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info) return null;

  const tooltip = `${info.git_sha} · built ${info.built_at}`;
  return (
    <span
      title={tooltip}
      aria-label={`Version ${info.version}. ${tooltip}`}
      className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-gray-500 bg-gray-100 border border-gray-200 cursor-default select-none"
    >
      v{info.version}
    </span>
  );
};

export default VersionPill;
